import { AxiosRequestConfig } from 'axios';
import interceptor from '../restServiceInterceptor';
import { showBackendError } from './backend-error';
import {
    FleetDriver,
    FleetKmReport,
    FleetStatus,
    FleetVehicle,
    FleetWaybill,
    FleetWaybillMonth,
    FleetWaybillSettings,
    FleetWaybillSettingsRequest,
} from '../models/fleet-models';
import { DistributorLocationPoint, RouteBranchMarker, RouteSummary } from '../models/distributor-location-models';

// Всичко за GPS Areon минава през work-service /fleet; уебът никога не вика GPS Areon директно.

/**
 * Записите и PDF-ите са без общото съобщение за грешка: то показва само превода на кода („Грешно въведени
 * данни“), а при 409 - нищо, докато причината (напр. от GPS Areon „Вече има шофьор с това име“) е в текста.
 * Екранът я показва с showFleetError.
 */
const OWN_ERROR: AxiosRequestConfig = { showOwnError: true };

/** Показва причината за грешката; при PDF тялото на грешката идва като Blob и първо се прочита. */
export const showFleetError = async (error: unknown) => {
    if (error instanceof Blob) {
        try {
            showBackendError(JSON.parse(await error.text()));
        } catch {
            showBackendError(undefined);
        }
        return;
    }
    showBackendError(error);
};

export const fetchFleetVehicles = (config?: AxiosRequestConfig): Promise<FleetVehicle[]> => {
    return interceptor.get('/work-service/fleet/vehicles', config);
};

/** userId null маха шофьора от колата. Важи от момента на запазване. */
export const assignFleetVehicleDriver = (deviceIdentifier: string, userId: number | null): Promise<FleetVehicle> => {
    return interceptor.put(`/work-service/fleet/vehicles/${deviceIdentifier}/driver`, { userId }, OWN_ERROR);
};

export const fetchFleetDrivers = (): Promise<FleetDriver[]> => {
    return interceptor.get('/work-service/fleet/drivers');
};

export const linkFleetDriver = (userId: number, gpsDriverId: string): Promise<FleetDriver> => {
    return interceptor.post('/work-service/fleet/drivers/links', { userId, gpsDriverId }, OWN_ERROR);
};

/** Създава шофьор в GPS Areon с името на потребителя и го свързва с него. */
export const createFleetDriverForUser = (userId: number): Promise<FleetDriver> => {
    return interceptor.post(`/work-service/fleet/drivers/links/${userId}/create-driver`, {}, OWN_ERROR);
};

export const unlinkFleetDriver = (userId: number): Promise<void> => {
    return interceptor.delete(`/work-service/fleet/drivers/links/${userId}`, OWN_ERROR);
};

export const fetchFleetStatus = (): Promise<FleetStatus> => {
    return interceptor.get('/work-service/fleet/status');
};

/** Маршрутът за деня: точките от колата, докато човекът е бил неин шофьор, и от телефона през останалото време. */
export const fetchFleetUserRoute = (userId: number, date: string): Promise<DistributorLocationPoint[]> => {
    return interceptor.get(`/work-service/fleet/users/${userId}/route`, { params: { date } });
};

/** Обобщение на деня по кола и по телефон: разстояние, движение, спирки, превишения и пътните листи. */
export const fetchFleetUserRouteSummary = (userId: number, date: string): Promise<RouteSummary> => {
    return interceptor.get(`/work-service/fleet/users/${userId}/route/summary`, { params: { date } });
};

export const fetchFleetUserBranchMarkers = (userId: number, date: string): Promise<RouteBranchMarker[]> => {
    return interceptor.get(`/work-service/fleet/users/${userId}/branch-markers`, { params: { date } });
};

export const fetchFleetWaybills = (from: string, to: string, driverUserId?: number): Promise<FleetWaybill[]> => {
    return interceptor.get('/work-service/fleet/waybills', { params: { from, to, driverUserId } });
};

export const deleteFleetWaybill = (id: string): Promise<void> => {
    return interceptor.delete(`/work-service/fleet/waybills/${id}`, OWN_ERROR);
};

/** PDF-ът за печат, както го генерира GPS Areon (данни, подписи, кой и кога ги е положил). */
export const fetchFleetWaybillPdf = (id: string): Promise<Blob> => {
    return interceptor.get(`/work-service/fleet/waybills/${id}/pdf`, { ...OWN_ERROR, responseType: 'blob' });
};

export const fetchFleetWaybillSettings = (): Promise<FleetWaybillSettings> => {
    return interceptor.get('/work-service/fleet/settings/waybills');
};

/** Записва всичко наведнъж в GPS Areon: празно поле изтрива стойността там. */
export const updateFleetWaybillSettings = (request: FleetWaybillSettingsRequest): Promise<FleetWaybillSettings> => {
    return interceptor.put('/work-service/fleet/settings/waybills', request, OWN_ERROR);
};

/** month е гггг-мм. */
export const fetchFleetWaybillMonths = (month: string): Promise<FleetWaybillMonth[]> => {
    return interceptor.get('/work-service/fleet/waybills/months', { params: { month } });
};

/** Месечният пътен лист на колата за печат (A4 легнал), както го генерира GPS Areon. */
export const fetchFleetWaybillMonthPdf = (deviceIdentifier: string, month: string): Promise<Blob> => {
    return interceptor.get(`/work-service/fleet/waybills/months/${deviceIdentifier}/${month}/pdf`, {
        ...OWN_ERROR,
        responseType: 'blob',
    });
};

export const fetchFleetKmReport = (from: string, to: string, driverUserId?: number): Promise<FleetKmReport> => {
    return interceptor.get('/work-service/fleet/reports/km', { params: { from, to, driverUserId } });
};
